import { Button, PopoverMenuProps } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone';
import dynamic from 'next/dynamic';
import { ReactNode, useState } from 'react';
import {
  PROFILE_POPOVER_MAX_WIDTH,
  PROFILE_POPOVER_MIN_WIDTH,
} from '@utils/constants';
import s from './MoreItemsButton.module.scss';

const PopoverMenu = dynamic<PopoverMenuProps>(
  () =>
    import('@wix/design-system').then((mod) => ({
      default: mod.PopoverMenu,
    })),
  { ssr: false },
);

interface MoreItemsButtonProps<T> {
  items: T[];
  maxVisibleItems: number;
  isMobileView?: boolean;
  renderItem: (item: T, index: number) => ReactNode;
  className?: string;
  onShowMore?: () => void;
  onShowLess?: () => void;
}

const MoreItemsButton = <T,>({
  items,
  maxVisibleItems,
  isMobileView,
  renderItem,
  className,
  onShowMore,
  onShowLess,
}: MoreItemsButtonProps<T>): JSX.Element => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  const visibleItems =
    showAll && isMobileView ? items : items.slice(0, maxVisibleItems);
  const remainingItems = items.slice(maxVisibleItems);
  const hasMoreItems = items.length > maxVisibleItems;

  const handleShowMore = () => {
    onShowMore?.(); // for mobile
    setShowAll(true);
  };

  const showMoreButton = (
    <Button
      className={s.showButton}
      dataHook="tag-more-count"
      key="show-more-button"
      size={isMobileView ? 'small' : 'tiny'}
      priority="secondary"
      skin="dark"
      onClick={handleShowMore}
    >
      {t('profile-page-services-more-count-label', {
        moreCount: items.length - maxVisibleItems,
      })}
    </Button>
  );

  const handleShowLess = () => {
    onShowLess?.(); // for mobile
    setShowAll(false);
  };

  const showLessButton = (
    <Button
      className={s.showButton}
      key="show-less-button"
      dataHook="tag-show-less"
      size={isMobileView ? 'small' : 'tiny'}
      priority="secondary"
      skin="dark"
      onClick={handleShowLess}
    >
      {t('profile-page-services-show-less-label')}
    </Button>
  );

  return (
    <div className={`${s.root} ${className || ''}`}>
      {visibleItems.map((item, index) => renderItem(item, index))}
      {hasMoreItems && (
        <>
          {isMobileView ? (
            <>{showAll ? showLessButton : showMoreButton}</>
          ) : (
            !showAll && (
              <PopoverMenu
                className={s.popoverMenu}
                appendTo="window"
                placement="left"
                minWidth={PROFILE_POPOVER_MIN_WIDTH}
                maxWidth={PROFILE_POPOVER_MAX_WIDTH}
                triggerElement={showMoreButton}
                onShow={onShowMore}
                onHide={onShowLess}
              >
                <div className={s.popoverContent} role="presentation">
                  {remainingItems.map((item, index) =>
                    renderItem(item, index + maxVisibleItems),
                  )}
                </div>
              </PopoverMenu>
            )
          )}
        </>
      )}
    </div>
  );
};

export default MoreItemsButton;
